import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useColorMode, Box, useBreakpointValue } from '@chakra-ui/react';

// Register required components for Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const MonthlySpendChart = ({ monthlySpendData }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Get month names
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  // Create a full year of data with zeros
  const defaultData = monthNames.map((month, index) => ({
    month: fullMonthNames[index],
    totalSpend: 0
  }));

  // Map the actual data to the correct months
  const mappedData = defaultData.map(defaultMonth => {
    const matchingData = monthlySpendData?.find(data => 
      data.month.toLowerCase() === defaultMonth.month.toLowerCase()
    );
    return matchingData || defaultMonth;
  });

  const data = {
    labels: mappedData.map(item => {
      const monthIndex = fullMonthNames.indexOf(item.month);
      return isMobile ? monthNames[monthIndex] : monthNames[monthIndex];
    }),
    datasets: [
      {
        label: 'Monthly Spend',
        data: mappedData.map(item => parseFloat(item.totalSpend || 0)),
        backgroundColor: isDark ? 'rgba(49, 130, 206, 0.7)' : 'rgba(49, 130, 206, 0.4)',
        borderColor: isDark ? 'rgba(49, 130, 206, 1)' : 'rgba(49, 130, 206, 0.8)',
        borderWidth: 1,
        borderRadius: 4,
        hoverBackgroundColor: isDark ? 'rgba(66, 153, 225, 0.8)' : 'rgba(66, 153, 225, 0.6)',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: isMobile ? 'y' : 'x',
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Monthly Spending Overview',
        color: isDark ? '#FFFFFF' : '#2D3748',
        font: {
          size: 14,
          weight: 'bold',
        },
        padding: { top: 10, bottom: 30 },
      },
      tooltip: {
        backgroundColor: isDark ? 'rgba(45, 55, 72, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        titleColor: isDark ? '#FFFFFF' : '#2D3748',
        bodyColor: isDark ? '#FFFFFF' : '#2D3748',
        bodyFont: {
          size: 12,
        },
        padding: 12,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          label: (context) => `SGD ${context.raw.toFixed(2)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: isDark ? '#FFFFFF' : '#2D3748',
          font: {
            size: 11,
            weight: 'bold',
          },
          padding: 8,
          maxRotation: 0,
          callback: (value) => {
            if(isMobile) {
              return `SGD ${value.toFixed(2)}`;
            } else {
              return monthNames[value];
            }
          }
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: isDark ? '#FFFFFF' : '#2D3748',
          font: {
            size: 11,
            weight: 'bold',
          },
          padding: 8,
          callback: (value, index) => {
            if (isMobile) {
              return monthNames[index];
            } else {
              return `SGD ${value.toFixed(2)}`;
            }
          },
        },
        reverse: isMobile,
      },
    },
    layout: {
      padding: {
        left: isMobile ? 10 : 10,
        right: 10,
        top: 0,
        bottom: 0,
      },
    },
    animation: {
      duration: 750,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <Box 
      position="relative" 
      width="100%" 
      height={isMobile ? "400px" : "300px"}
      minHeight={isMobile ? "400px" : "300px"}
      maxHeight={isMobile ? "500px" : "400px"}
      // mb={32}
      // mt={16}
      // p={6}
      bg={isDark ? "whiteAlpha.50" : "gray.50"}
      borderRadius="lg"
    >
      <Box 
        position="relative"
        height="100%"
        width="100%"
      >
        <Bar data={data} options={options} />
      </Box>
    </Box>
  );
};

export default MonthlySpendChart;
